
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

/**
 * Hook to handle authentication redirects
 * @param redirectToPath Path to redirect to if user is not authenticated
 * @param shouldToast Whether to show a toast message on redirect
 * @param toastMessage Custom toast message (defaults to "Please sign in to continue")
 */
export const useAuthRedirect = (
  redirectToPath: string = '/auth',
  shouldToast: boolean = false,
  toastMessage: string = "Please sign in to continue"
) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const isNavigating = useRef(false);

  useEffect(() => {
    // Create a function to check auth status
    const checkAuthStatus = async () => {
      try {
        // If we're already navigating, don't trigger another navigation
        if (isNavigating.current) return;

        const { data: { session } } = await supabase.auth.getSession();
        
        // If no session, redirect to auth page
        if (!session) {
          console.log("No authenticated session found, redirecting to", redirectToPath);
          isNavigating.current = true;
          
          if (shouldToast) {
            toast({
              title: "Authentication required",
              description: toastMessage,
              variant: "default",
            });
          }
          
          navigate(redirectToPath);
        }
      } catch (err) {
        console.error("Auth check error:", err);
      } finally {
        // Reset the navigation flag after a short delay
        setTimeout(() => {
          isNavigating.current = false;
        }, 1000);
      }
    };
    
    // Check auth status on mount
    checkAuthStatus();
    
    // Set up listener for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        // On sign out, redirect to auth page if not already navigating
        if (!isNavigating.current) {
          isNavigating.current = true;
          navigate(redirectToPath);
        }
      }
    });
    
    return () => {
      subscription.unsubscribe();
    };
  }, [navigate, redirectToPath, shouldToast, toast, toastMessage]);
};
